import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import TrackedNotice from './TrackedNotice';

const SchoolNews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const student = location.state?.student;

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!student) {
      navigate('/student-login');
      return;
    }
    loadAnnouncements();
  }, [student, navigate]);

  const loadAnnouncements = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/announcements/${student.username}`, { credentials: 'include' });
      if (!response.ok) throw new Error('Please sign in again to load school news.');
      const announcementsData = await response.json();
      setAnnouncements(announcementsData);
      setLoadError('');
    } catch (error) {
      console.error('Error loading announcements:', error);
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredAnnouncements = () => {
    let filtered = announcements;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(announcement => announcement.category === selectedCategory);
    }

    // Filter by read status
    if (showUnreadOnly) {
      filtered = filtered.filter(announcement => !announcement.isRead);
    }

    // Sort by priority and date
    return filtered.sort((a, b) => {
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 1;
      const bPriority = priorityOrder[b.priority] || 1;

      if (aPriority !== bPriority) return bPriority - aPriority;

      return new Date(b.publishDate) - new Date(a.publishDate);
    });
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return '🚨';
      case 'high': return '⚡';
      case 'normal': return '📢';
      case 'low': return 'Note';
      default: return '📢';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'border-red-500 bg-red-50';
      case 'high': return 'border-orange-500 bg-orange-50';
      case 'normal': return 'border-blue-500 bg-blue-50';
      case 'low': return 'border-gray-500 bg-gray-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'academic': return 'Final Exam';
      case 'events': return 'Success';
      case 'sports': return '⚽';
      case 'emergency': return '🚨';
      case 'general': return '📢';
      default: return '📢';
    }
  };

  if (!student) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-xl text-blue-600">Loading school news...</div>
      </div>
    );
  }

  const filteredAnnouncements = getFilteredAnnouncements();
  const unreadCount = announcements.filter(a => !a.isRead).length;

  return (
    <div className="min-h-screen bg-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-blue-900">School News & Announcements</h1>
              <p className="text-sm text-gray-600">Stay updated with school announcements and news</p>
            </div>
            <button
              onClick={() => navigate('/student-dashboard', { state: { student } })}
              className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loadError && <p role="alert" className="text-red-700 mb-4">{loadError} <button className="underline" onClick={() => navigate('/student-login')}>Sign in</button></p>}
        {/* Stats & Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center">
              <div className="text-2xl mb-2">📬</div>
              <div className="text-2xl font-bold text-blue-600">{announcements.length}</div>
              <div className="text-sm text-gray-600">Total Announcements</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-2">Study</div>
              <div className="text-2xl font-bold text-green-600">{unreadCount}</div>
              <div className="text-sm text-gray-600">Unread</div>
            </div>
            <div className="text-center">
              <div className="text-2xl mb-2">Loading</div>
              <div className="text-2xl font-bold text-purple-600">{announcements.length - unreadCount}</div>
              <div className="text-sm text-gray-600">Read</div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                <option value="general">General</option>
                <option value="academic">Academic</option>
                <option value="events">Events</option>
                <option value="sports">Sports</option>
                <option value="emergency">Emergency</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showUnreadOnly}
                  onChange={(e) => setShowUnreadOnly(e.target.checked)}
                  className="rounded text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Show unread only</span>
              </label>
            </div>
          </div>
        </div>

        {/* Announcements List */}
        <div className="space-y-6">
          {filteredAnnouncements.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">
                {showUnreadOnly ? '?' : 'Inbox'}
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {showUnreadOnly ? 'All Caught Up!' : 'No Announcements Found'}
              </h3>
              <p className="text-gray-600 mb-6">
                {showUnreadOnly
                  ? 'You\'ve read all announcements. Great job staying updated!'
                  : 'There are no announcements matching your current filters.'}
              </p>
              {showUnreadOnly && (
                <button
                  onClick={() => setShowUnreadOnly(false)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition duration-200"
                >
                  View All Announcements
                </button>
              )}
            </div>
          ) : (
            filteredAnnouncements.map((announcement) => (
              <TrackedNotice
                key={announcement.id}
                item={announcement}
                onReceipt={receipt => setAnnouncements(current => current.map(item => item.id === announcement.id ? { ...item, ...receipt } : item))}
                className={`border-l-4 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 ${
                  getPriorityColor(announcement.priority)
                } ${!announcement.isRead ? 'bg-blue-50' : 'bg-white'}`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">{getPriorityIcon(announcement.priority)}</span>
                        <h3 className="text-xl font-bold text-gray-900">{announcement.title}</h3>
                        {!announcement.isRead && (
                          <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded-full">
                            New
                          </span>
                        )}
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                        <span className="flex items-center space-x-1">
                          <span>{getCategoryIcon(announcement.category)}</span>
                          <span>{announcement.category}</span>
                        </span>
                        <span>By {announcement.author}</span>
                        <span>{new Date(announcement.publishDate).toLocaleDateString()}</span>
                        {announcement.views && (
                          <span>👁️ {announcement.views} views</span>
                        )}
                      </div>
                    </div>

                  </div>

                  {/* Content */}
                  <div className="text-gray-700 mb-4 leading-relaxed">
                    {announcement.content.split('\n').map((paragraph, index) => (
                      <p key={index} className={index > 0 ? 'mt-3' : ''}>
                        {paragraph}
                      </p>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-between items-center text-sm text-gray-600 pt-4 border-t border-gray-200">
                    <div>
                      {announcement.expiryDate && (
                        <span>
                          Expires: {new Date(announcement.expiryDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {announcement.isRead ? (
                        <span className="text-green-600">? Read</span>
                      ) : (
                        <span className="text-blue-600">Study Unread</span>
                      )}
                    </div>
                  </div>
                </div>
              </TrackedNotice>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default SchoolNews;



